import { formatKathmanduWhen } from "./nepal-time";

export type VehicleTrackStatus = "moving" | "stopped" | "offline";

export interface TrailPoint {
  lat: number;
  lon: number;
}

export const MOVING_SPEED_KMH = 3;
export const MAX_TRAIL_POINTS = 400;
/** Live-trip path: keep the route from the moment follow starts, without a second GPS feed. */
export const LIVE_TRIP_MIN_M = 4;
export const LIVE_TRIP_MAX_POINTS = 4000;
/** Skip interpolation when a fix jumps farther than this (bad GPS / first lock). */
export const SNAP_DISTANCE_M = 2_000;
const EARTH_M = 6_371_000;

export function vehicleTrackStatus(args: {
  gpsLive: boolean;
  speedKmh: number | null | undefined;
  movingThresholdKmh?: number;
}): VehicleTrackStatus {
  if (!args.gpsLive) return "offline";
  const threshold = args.movingThresholdKmh ?? MOVING_SPEED_KMH;
  if (args.speedKmh != null && args.speedKmh >= threshold) return "moving";
  return "stopped";
}

export function vehicleTrackLabel(status: VehicleTrackStatus): string {
  if (status === "moving") return "Moving";
  if (status === "stopped") return "Stopped";
  return "Last known";
}

export function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function haversineMeters(a: TrailPoint, b: TrailPoint): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLon * sinLon;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/** Shortest-path heading interpolation. Headings are 0–360, 0 = north. */
export function lerpHeading(from: number, to: number, t: number): number {
  const delta = ((((to - from) % 360) + 540) % 360) - 180;
  return (from + delta * t + 360) % 360;
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function durationForMoveMs(from: TrailPoint, to: TrailPoint, speedKmh: number | null | undefined): number {
  const meters = haversineMeters(from, to);
  if (meters < 1) return 200;
  if (meters >= SNAP_DISTANCE_M) return 0;
  const mps = speedKmh != null && speedKmh >= MOVING_SPEED_KMH ? (speedKmh * 1000) / 3600 : 8;
  return clamp((meters / mps) * 1000, 400, 8_000);
}

export function compassAbbrev(headingDeg: number | null | undefined): string | null {
  if (headingDeg == null || !Number.isFinite(headingDeg)) return null;
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;
  const i = Math.round((((headingDeg % 360) + 360) % 360) / 45) % 8;
  return dirs[i] ?? "N";
}

export function formatSpeedKmh(speedKmh: number | null | undefined): string {
  if (speedKmh == null || !Number.isFinite(speedKmh)) return "—";
  return `${speedKmh.toFixed(1)} km/h`;
}

export function formatHeading(headingDeg: number | null | undefined): string {
  if (headingDeg == null || !Number.isFinite(headingDeg)) return "—";
  const dir = compassAbbrev(headingDeg);
  return dir ? `${Math.round(headingDeg)}° ${dir}` : `${Math.round(headingDeg)}°`;
}

export function formatGpsAge(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "No update yet";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "No update yet";
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 5) return "Just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  return formatKathmanduWhen(new Date(t).toISOString());
}

export function isValidPoint(p: TrailPoint | null | undefined): p is TrailPoint {
  if (!p) return false;
  return Number.isFinite(p.lat) && Number.isFinite(p.lon) && Math.abs(p.lat) <= 90 && Math.abs(p.lon) <= 180;
}

export function sanitizeTrail(points: TrailPoint[], max = MAX_TRAIL_POINTS): TrailPoint[] {
  const out: TrailPoint[] = [];
  for (const p of points) {
    if (!isValidPoint(p)) continue;
    const prev = out[out.length - 1];
    if (prev) {
      const d = haversineMeters(prev, p);
      if (d < 2) continue;
      if (d >= SNAP_DISTANCE_M) {
        out.length = 0;
      }
    }
    out.push({ lat: p.lat, lon: p.lon });
  }
  return out.length > max ? out.slice(out.length - max) : out;
}

export function appendTrailPoint(
  trail: TrailPoint[],
  point: TrailPoint,
  max = MAX_TRAIL_POINTS,
  minMeters = 2,
): TrailPoint[] {
  if (!isValidPoint(point)) return trail;
  const last = trail[trail.length - 1];
  if (last && haversineMeters(last, point) < minMeters) return trail;
  const next = last && haversineMeters(last, point) >= SNAP_DISTANCE_M ? [point] : [...trail, point];
  return next.length > max ? next.slice(next.length - max) : next;
}

/** Real satellite fix only. Missing bits and 0,0 are not a position to drive the marker with. */
export function isUsableGpsFix(
  pos: { lat: number; lon: number; gps_fix?: boolean; status_bits?: number | null } | null | undefined,
): boolean {
  if (!pos || pos.gps_fix === false) return false;
  if (!isValidPoint(pos)) return false;
  if (Math.abs(pos.lat) < 1e-7 && Math.abs(pos.lon) < 1e-7) return false;
  if (pos.gps_fix === true) return true;
  if (pos.status_bits == null || !Number.isFinite(pos.status_bits)) return false;
  return (pos.status_bits & 0x2) !== 0;
}

/** Append the marker's current real position. A lost fix keeps the path already drawn. */
export function extendLiveTripTrail(trail: TrailPoint[], point: TrailPoint | null, gpsOk: boolean): TrailPoint[] {
  if (!gpsOk || !point) return trail;
  return appendTrailPoint(trail, point, LIVE_TRIP_MAX_POINTS, LIVE_TRIP_MIN_M);
}

export function mergeTrails(
  history: TrailPoint[],
  live: TrailPoint[] | undefined,
  head?: TrailPoint | null,
): TrailPoint[] {
  let merged = history.slice();
  for (const p of live ?? []) merged = appendTrailPoint(merged, p);
  if (head && isValidPoint(head)) merged = appendTrailPoint(merged, head, MAX_TRAIL_POINTS + 1);
  return merged;
}
